import { useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import useGetMerchantUsers from "@/services/merchantUser/hooks/useGetMerchantUsers";
import { moveItem, saveStaffOrder } from "@/services/merchantUser/portfolio";
import { MerchantUserDto } from "@/models/club";

export default function StaffOrder() {
  const { isManager } = useDashboardAccess();
  const { data, isLoading, isError, refetch } = useGetMerchantUsers();
  const client = useQueryClient();
  const [opened, setOpened] = useState(false);
  const [pending, setPending] = useState<MerchantUserDto[] | null>(null);
  const [error, setError] = useState("");
  const locked = useRef(false);
  const dragged = useRef<number | null>(null);
  const members = pending ?? data ?? [];
  const move = async (from: number, to: number) => {
    if (locked.current || !isManager || from === to || !data) return;
    const next = moveItem(data, from, to);
    if (next.some((member) => !member.id)) return;
    locked.current = true;
    setPending(next);
    setError("");
    try {
      await saveStaffOrder(next.map((member) => member.id!));
      client.setQueryData(["getMerchantUsers"], next);
      // Calendars and settlement summaries also inherit the team order.
      await client.invalidateQueries();
    } catch {
      setPending(data);
      setError("ترتیب ذخیره نشد؛ ترتیب قبلی بازگردانده شد.");
      await refetch();
    } finally {
      locked.current = false;
      setPending(null);
    }
  };
  if (!isManager) return null;
  return (
    <>
      <Button variant="light" onClick={() => setOpened(true)}>
        ترتیب نمایش تیم
      </Button>
      <Modal
        opened={opened}
        onClose={() => {
          if (!locked.current) setOpened(false);
        }}
        title="ترتیب نمایش تیم"
        closeOnClickOutside={!pending}
        closeOnEscape={!pending}
        withCloseButton={!pending}
      >
        <Stack>
          <Text size="sm">
            همهٔ افراد تیم، حتی کسانی که از سایت مخفی هستند، در این فهرست هستند. تغییر ترتیب
            با کشیدن یا دکمه‌های بالا و پایین، خودکار ذخیره می‌شود.
          </Text>
          {(error || isError) && (
            <Alert color="red" role="alert">
              {error || "دریافت تیم انجام نشد."}
              <Button variant="subtle" onClick={() => void refetch()}>
                تلاش مجدد
              </Button>
            </Alert>
          )}
          {isLoading && <Text>در حال دریافت تیم…</Text>}
          {members.map((member, index) => (
            <Paper
              key={member.id}
              withBorder
              p="sm"
              draggable={!pending}
              onDragStart={(event) => {
                dragged.current = index;
                event.dataTransfer.setData("text/plain", String(member.id));
              }}
              onDragEnd={() => {
                dragged.current = null;
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                if (dragged.current !== null) void move(dragged.current, index);
                dragged.current = null;
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text>
                    {member.firstName} {member.lastName}
                  </Text>
                  {member.showOnWebsite === false && (
                    <Badge color="gray">مخفی از سایت</Badge>
                  )}
                </Group>
                <Group>
                  <Button
                    variant="default"
                    size="xs"
                    disabled={!!pending || index === 0 || isError}
                    onClick={() => void move(index, index - 1)}
                  >
                    بالا
                  </Button>
                  <Button
                    variant="default"
                    size="xs"
                    disabled={
                      !!pending || index === members.length - 1 || isError
                    }
                    onClick={() => void move(index, index + 1)}
                  >
                    پایین
                  </Button>
                </Group>
              </Stack>
            </Paper>
          ))}
          {!isLoading && !isError && !members.length && (
            <Text>هنوز کسی به تیم اضافه نشده است.</Text>
          )}
          {pending && <Text role="status">در حال ذخیره ترتیب…</Text>}
        </Stack>
      </Modal>
    </>
  );
}
